/**
 * Ordinary manual saved-plan Swap: user-facing projected-day presentation.
 *
 * All values here are derived from the NEW backend Swap contract
 * (nutrition_state / projected_pcts + ranges.on_track and the per-candidate
 * safety / preference facts). The Swap nutrition math is NOT recreated in
 * React — we only MAP the backend's authoritative facts onto the three
 * user-facing status labels.
 */
import { getMealConflicts, hasAllergenConflict } from './mealConflicts';

export var SWAP_STATUS = {
  WITHIN: 'Within Range',
  UNDER: 'Under Target',
  OUT: 'Out of Range',
};

function inBand(pct, band) {
  return band && typeof pct === 'number' && pct >= band[0] && pct <= band[1];
}

/**
 * Map a Swap candidate to one of the three user-facing nutrition states,
 * or null when a status must not be fabricated.
 *
 * Phase 2B: the backend's three-state `nutrition_state` (the same fact its
 * server order groups by) is authoritative, so a label never contradicts the
 * card's place in the list:
 *   WITHIN_RANGE                              -> Within Range
 *   UNDER_TARGET (nothing over, something under) -> Under Target
 *   OUT_OF_RANGE (any reason)                 -> Out of Range
 *
 * An older response without nutrition_state keeps the legacy calorie-first
 * mapping from projected_pcts and the goal's On Track band:
 *   everything inside range            -> Within Range
 *   calories below On Track range      -> Under Target
 *   calories above On Track range      -> Out of Range
 *   calories in range, P/C/F not all in-> Out of Range
 *
 * opts = { targetsReady: bool, ranges: { on_track: {calories:[lo,hi], ...} } }
 */
export function getSwapStatusLabel(meal, opts) {
  opts = opts || {};
  if (!opts.targetsReady) return null;
  if (!meal) return null;
  var state = meal.nutrition_state;
  if (state === 'WITHIN_RANGE') return SWAP_STATUS.WITHIN;
  if (state === 'UNDER_TARGET') return SWAP_STATUS.UNDER;
  if (state === 'OUT_OF_RANGE') return SWAP_STATUS.OUT;
  if (!meal.projected_pcts) return null;
  var onTrack = opts.ranges && opts.ranges.on_track;
  if (!onTrack || !onTrack.calories) return null;

  var pcts = meal.projected_pcts;
  var calBand = onTrack.calories;
  var calPct = pcts.calories;
  if (typeof calPct !== 'number') return null;

  if (calPct < calBand[0]) return SWAP_STATUS.UNDER;
  if (calPct > calBand[1]) return SWAP_STATUS.OUT;

  // Calories are inside the acceptable range: the day is Within Range only
  // when protein, carbs AND fat are also inside their On Track bands.
  var allInside = ['protein', 'carbs', 'fat'].every(function (k) {
    return inBand(pcts[k], onTrack[k]);
  });
  return allInside ? SWAP_STATUS.WITHIN : SWAP_STATUS.OUT;
}

/**
 * Greying is guidance, not a hard block — greyed cards remain fully
 * selectable. Founder rule: only OVER conditions grey a card; a deliberate
 * under choice never does.
 *   - calories over range                                    -> grey
 *   - any P/C/F macro over its On Track upper bound            -> grey
 *   - under-only nutrition deviation                          -> NOT grey
 *   - Out of Range caused only by a LOW macro                 -> NOT grey
 *   - Under Target calories but another macro already over    -> grey
 * We reuse the backend candidate's `over_macros` (macros above their On Track
 * ceiling; includes "calories") — never recreate tolerance math. Allergen
 * (a matched token OR allergen safety the backend could not verify),
 * dislike, missing cookware, exceeds_cook_time (saved preference) and a miss
 * on the ACTIVE cook-time filter (backend filter_cook_time_match === false;
 * null when no cook-time filter is active) always grey regardless.
 * Don't Suggest is grey guidance while remaining selectable. A same-day
 * family / group repeat is labelled but never greys a card by itself.
 */
export function shouldGreyCard(meal, statusLabel) {
  if (!meal) return false;
  // Hard non-nutrition issues always grey.
  if (hasAllergenConflict(meal)) return true;
  if (getMealConflicts(meal).some(function (c) { return c.key === 'dont_suggest'; })) return true;
  if (meal.has_dislike) return true;
  if (meal.missing_cookware && meal.missing_cookware.some(function(cw) { return cw !== 'no_cook'; })) return true;
  if (meal.exceeds_cook_time) return true;
  if (meal.filter_cook_time_match === false) return true;
  // Nutrition greying: ONLY when something is over (never under-only).
  var over = meal.over_macros || [];
  if (over.length > 0) return true;
  return false;
}

// Swap bubble priority: allergen > dislike > Don't Suggest > cookware >
// active cook-time filter miss > saved cook-time preference > same-day repeats.
var SWAP_BUBBLE_ORDER = ['allergen', 'dislike', 'dont_suggest', 'cookware', 'cooktime_filter', 'cooktime',
  'same_day_family', 'same_day_group'];
// mealConflicts keys -> the Swap bubble's existing keys.
var SWAP_BUBBLE_KEY = { missing_cookware: 'cookware', exceeds_cook_time: 'cooktime' };

/**
 * Every non-nutrition note for a Swap card in bubble priority: the backend's
 * manual-choice conflicts (mealConflicts) plus a miss on the ACTIVE cook-time
 * filter. Each entry: { key, label, tone, detail }.
 */
export function getSwapConflictEntries(meal) {
  if (!meal) return [];
  var entries = getMealConflicts(meal).map(function (c) {
    return { key: SWAP_BUBBLE_KEY[c.key] || c.key, label: c.label, tone: c.tone, detail: c.detail };
  });
  if (meal.filter_cook_time_match === false) {
    entries.push({ key: 'cooktime_filter',
      label: meal.filter_cook_time_unknown ? 'Cook time unknown' : 'Outside selected cook time',
      tone: 'warn', detail: [] });
  }
  entries.sort(function (a, b) {
    return SWAP_BUBBLE_ORDER.indexOf(a.key) - SWAP_BUBBLE_ORDER.indexOf(b.key);
  });
  return entries;
}

/**
 * The SINGLE highest-priority non-nutrition warning bubble for a Swap card
 * (nutrition status lives in the card body, never here). Returns
 * { key, label, tone } — plus `more` (how many further notes the card has)
 * when there are several — or null.
 */
export function getSwapWarningBubble(meal) {
  var entries = getSwapConflictEntries(meal);
  if (!entries.length) return null;
  var top = entries[0];
  var bubble = { key: top.key, label: top.label, tone: top.tone };
  if (entries.length > 1) bubble.more = entries.length - 1;
  return bubble;
}

/**
 * Cook time for the right side of the macro row.
 *   - reliably no-cook recipe -> "No cook"
 *   - a real positive time    -> "N min"
 *   - unknown/missing          -> null (never invent "0 min")
 */
export function getCookTimeDisplay(meal) {
  if (!meal) return null;
  var cw = (meal.required_cookware || []).map(function (x) {
    return String(x || '').toLowerCase().replace(/[\s-]/g, '_');
  });
  if (cw.indexOf('no_cook') !== -1) return 'No cook';
  var t = meal.cook_time_min;
  if (t == null) t = meal.cook_time_minutes;
  if (typeof t === 'number' && t > 0) return t + ' min';
  return null;
}

/** Compact, icon-free macro row text: "590 cal · 37P · 74C · 18F". */
export function formatMacroRow(meal) {
  if (!meal) return '';
  var r = function (v) { return Math.round(v || 0); };
  return r(meal.calories) + ' cal · ' + r(meal.protein) + 'P · ' +
    r(meal.carbs) + 'C · ' + r(meal.fat) + 'F';
}

/**
 * Swap results header text driven by the NEW backend counts. Returns null
 * when targets are not ready (caller falls back to neutral Browse wording).
 * data = { targets_ready, day_complete, on_track_count, compatible_count, meal_count }
 */
export function getSwapHeaderText(data) {
  if (!data || !data.targets_ready) return null;
  if (data.day_complete) {
    var n = data.on_track_count || 0;
    return n + (n === 1 ? ' option keeps' : ' options keep') + ' your day within range';
  }
  var c = (data.compatible_count != null) ? data.compatible_count : (data.meal_count || 0);
  return c + (c === 1 ? ' option fits' : ' options fit') + ' this spot';
}
